import React from 'react';
import { Link } from 'react-router-dom';
import { useProductsByIds } from '../../hooks/useHomepageQueries';
import ProductCard from '../ui/ProductCard';
import { TooltipProvider } from '../ui/tooltip';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

const CategorySections = ({ categorySections = [], isLoading = false }) => {
  // Extract all product IDs from category sections
  const allProductIds = categorySections.flatMap(section =>
    section.productIds?.filter(id => id && typeof id === 'string') || []
  );

  // Fetch products for all category sections
  const {
    data: allProducts = [],
    isLoading: isProductsLoading
  } = useProductsByIds(allProductIds, allProductIds.length > 0);

  // Create a map of product ID to product object for quick lookup
  const productsMap = React.useMemo(() => {
    const map = new Map();
    allProducts.forEach(product => {
      map.set(product._id, product);
    });
    return map;
  }, [allProducts]);

  if (isLoading || isProductsLoading) {
    return (
      <section className="bg-white py-6 sm:py-8 md:py-10 lg:py-12">
        <div className="md:container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4 max-w-md"></div>
            <div className="h-4 bg-gray-200 rounded mb-8 max-w-lg"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-200 h-64 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!categorySections || categorySections.length === 0) {
    return null;
  }

  return (
    <>
      {categorySections.map((section, index) => {
        const sectionProducts = section.productIds
          ?.map(id => productsMap.get(id))
          .filter(Boolean) || [];

        if (sectionProducts.length === 0) {
          return null;
        }

        return (
          <section key={`${section.categoryId}-${index}`} className="bg-white py-4 sm:py-6 md:py-8">
            <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    {section.categoryName}
                  </h2>
                  <p className="text-gray-600 text-sm sm:text-base">
                    Discover our curated selection of {section.categoryName.toLowerCase()} products
                  </p>
                </div>
                <Link
                  to={`/category/${section.categoryId}`}
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base transition-colors"
                >
                  View All →
                </Link>
              </div>

              <TooltipProvider>
                <div className="relative group category-products-slider">
                  <Swiper
                    modules={[Navigation]}
                    spaceBetween={16}
                    slidesPerView={2}
                    navigation
                    breakpoints={{
                      640: { slidesPerView: 3, spaceBetween: 16 },
                      1024: { slidesPerView: 4, spaceBetween: 24 },
                      1280: { slidesPerView: 5, spaceBetween: 24 }
                    }}
                    className="py-2"
                  >
                    {sectionProducts.map((product) => (
                      <SwiperSlide key={product._id} className="h-auto">
                        <ProductCard product={product} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </TooltipProvider>

              <style dangerouslySetInnerHTML={{
                __html: `
                .category-products-slider .swiper-button-next,
                .category-products-slider .swiper-button-prev {
                  background-color: white;
                  width: 36px;
                  height: 36px;
                  border-radius: 50%;
                  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                  color: #1E3A8A;
                  border: 1px solid #e5e7eb;
                }
                .category-products-slider .swiper-button-next:after,
                .category-products-slider .swiper-button-prev:after {
                  font-size: 14px;
                  font-weight: bold;
                }
                .category-products-slider .swiper-button-disabled {
                  opacity: 0 !important;
                }
              `}} />

            </div>
          </section>
        );
      })}
    </>
  );
};

export default CategorySections; 